// mapelements/Wind.js
import React, { useEffect, useState, useRef } from "react";
import windIcon from '../../images/wind.svg';
import arrowIcon from '../../images/wind-arrow.png';

const Wind = ({ map }) => {
  // Load visibility state from localStorage or default to false
  const [isVisible, setIsVisible] = useState(
    localStorage.getItem("windVisible") === "true"
  );
  const [windData, setWindData] = useState(null); // State to store wind data
  const windUpdateInterval = useRef(null); // Ref to store the interval ID

  // Fetch wind data
  const fetchWindData = async () => {
    console.log("Loading Wind Data");
    try {
      const response = await fetch("https://flyk.com/api/metars.geojson");
      if (!response.ok) throw new Error("Failed to fetch wind data");
      const data = await response.json();

      // Filter out features with null windDirection or wind
      const filteredData = {
        ...data,
        features: data.features.filter(
          (feature) =>
            feature.properties.windDirection !== null &&
            feature.properties.wind !== null
        ),
      };

      setWindData(filteredData);
    } catch (error) {
      console.error("Error fetching wind data:", error);
    }
  };

  // Add or remove the wind layer
  const addWindLayer = () => {
    if (!map || !windData) return;

    // Add the arrow icon to the map
    map.loadImage(arrowIcon, (error, image) => {
      if (error) {
        console.error("Error loading arrow icon:", error);
        return;
      }

      if (!map.hasImage('arrow-icon')) {
        map.addImage('arrow-icon', image);
      }

      // Add the wind data source
      if (!map.getSource('wind')) {
        map.addSource('wind', {
          type: 'geojson',
          data: windData,
        });
      } else {
        map.getSource('wind').setData(windData);
      }

      // Add the wind layer
      if (!map.getLayer('wind-layer')) {
        map.addLayer({
          id: 'wind-layer',
          type: 'symbol',
          source: 'wind',
          layout: {
            'icon-image': 'arrow-icon',
            'icon-size': 0.4,
            'icon-rotate': [
              'coalesce',
              ['get', 'windDirection'],
              0,
            ],
            'icon-allow-overlap': true,
            'text-field': [
              'format',
              ['to-string', ['round', ['coalesce', ['get', 'wind'], 0]]],
              ' kt',
            ],
            'text-size': 10,
            'text-offset': [1, 0],
            'text-anchor': 'left',
            'text-allow-overlap': true,
            'text-font': ['Open Sans Bold'],
          },
          paint: {
            'text-color': 'black',
            'text-halo-color': 'white',
            'text-halo-width': 2,
          },
          minzoom: 5
        });
      }
    });
  };

  const removeWindLayer = () => {
    if (!map) return;

    if (map.getLayer('wind-layer')) {
      map.removeLayer('wind-layer');
    }
    if (map.getSource('wind')) {
      map.removeSource('wind');
    }
  };

  // Effect to handle visibility toggle
  useEffect(() => {
    if (!map) return;

    if (isVisible) {
      fetchWindData(); // Initial fetch
      windUpdateInterval.current = setInterval(fetchWindData, 6 * 60 * 1000); // Update every 6 minutes
    } else {
      removeWindLayer(); // Remove the wind layer
      if (windUpdateInterval.current) {
        clearInterval(windUpdateInterval.current); // Stop the interval
      }
    }

    // Cleanup on unmount
    return () => {
      removeWindLayer();
      if (windUpdateInterval.current) {
        clearInterval(windUpdateInterval.current);
      }
    };
  }, [map, isVisible]);

  // Effect to handle wind data updates
  useEffect(() => {
    if (!map || !isVisible || !windData) return;

    addWindLayer(); // Add the wind layer when data is available
  }, [map, isVisible, windData]);

  // Toggle wind visibility and save state to localStorage
  const toggleVisibility = () => {
    const newVisibility = !isVisible;
    setIsVisible(newVisibility);
    localStorage.setItem("windVisible", newVisibility.toString()); // Save state
  };

  return (
    <button
        data-tooltip-id="map-buttons-tooltip"
        data-tooltip-content="Surface Wind"
        className={`w-8 h-8 ${isVisible ? "bg-gray-600" : "bg-gray-400"} text-white flex items-center justify-center`}
        onClick={toggleVisibility}
    >
        <img className="w-6" src={windIcon} alt="Wind" />
    </button>
  );
};

export default Wind;